import { createApiRef } from '@backstage/core-plugin-api';
import type { UnifiedGithubApi } from '@runway/devkit';

const owner = 'AAInternal';
const repoName = 'graphql-policies';
const repoConnectorName = 'apollo-connectors';

export interface GraphqlProjectListQueryApi {
  requestPolicy: (formData: {
    selectedRows: any;
    clientId: string;
    archerShortName: string;
    userEmail: string;
    selectedPolicyNames: string[];
    env: string;
    githubTeam: string;
  }) => Promise<any>;
  getEnvDataJsonFile: (env: string) => Promise<any>;
  createPolicy: (formData: {
    policyName: string;
    shortName: string;
    description: string;
    email: string;
    githubName: string;
    env: string;
  }) => Promise<any>;
  getConnectorEnvDataJsonFile: (env: string) => Promise<any>;
  registerConnector: (formData: {
    connectorName: string;
    shortName: string;
    description: string;
    email: string;
    githubName: string;
    vaultNamespace: string;
    vaultSecretPath: string;
    authProvider: string;
    authURL: string;
    env: string;
  }) => Promise<any>;
}

export const GraphqlProjectListQueryApiRef = createApiRef<GraphqlProjectListQueryApi>({
  id: 'plugin.graphql.graphql.service',
});

export class GraphqlProjectsList implements GraphqlProjectListQueryApi {
  constructor(private unifiedGithubApi: UnifiedGithubApi) {}

  parseTeam(githubName: string): string {
    return githubName.split('/').pop() || '';
  }

  getEnvDataJsonFile = async (env: string) => {
    const data = await this.unifiedGithubApi.getFileInfo({
      owner,
      repo: repoName,
      filePath: `root/authz/data.json`,
      ref: env,
    });

    if (Array.isArray(data) || !('content' in data)) {
      throw new Error(`Failed to fetch data.json from branch ${env}: not a file`);
    }

    return JSON.parse(Buffer.from(data.content, 'base64').toString());
  };

  requestPolicy = async (formData: {
    selectedRows: any;
    clientId: string;
    archerShortName: string;
    userEmail: string;
    selectedPolicyNames: string[];
    env: string;
    githubTeam: string;
  }) => {
    const currentDate = new Date();
    const timestamp = currentDate.toISOString().replace(/[-:.TZ]/g, '');
    let pullRequestUrl = '';

    try {
      for (const row of formData.selectedPolicyNames) {
        const newBranchName = `update-${row}-${timestamp}`;

        await this.unifiedGithubApi.createBranch({
          owner,
          repo: repoName,
          branchName: newBranchName,
          fromRef: `heads/${formData.env}`,
        });

        const parsedTeam = this.parseTeam(formData.githubTeam);

        const newClientEntry = {
          clientid: formData.clientId,
          owner: `@${owner}/${parsedTeam}`,
          shortname: formData.archerShortName,
          email: formData.userEmail,
        };

        const filePath = `${row}/clients.json`;

        const fileInfo = await this.unifiedGithubApi.getFileInfo({
          owner,
          repo: repoName,
          filePath,
          ref: newBranchName,
        });

        if (Array.isArray(fileInfo) || !('content' in fileInfo)) {
          throw new Error(`Failed to fetch data.json from data structure: ${JSON.stringify(fileInfo)}`);
        }

        const clients = JSON.parse(Buffer.from(fileInfo.content, 'base64').toString());

        clients[row].clients.push(newClientEntry);

        const updatedJsonString = JSON.stringify(clients, null, 2);

        await this.unifiedGithubApi.updateFile({
          owner,
          repo: repoName,
          path: filePath,
          message: `Updated clients.json for policy: ${row}`,
          content: Buffer.from(updatedJsonString).toString('base64'),
          sha: fileInfo.sha,
          branch: newBranchName,
        });

        const pr = await this.unifiedGithubApi.openPullRequest({
          owner,
          repo: repoName,
          title: `fix: add new client to clients.json for ${row} policy`,
          head: newBranchName,
          base: formData.env,
        });

        pullRequestUrl = pr.html_url;
      }
    } catch (error) {
      const typedError = error as Error;

      return {
        success: false,
        error: typedError.message,
        pullRequestUrl: '',
      };
    }

    return {
      success: true,
      error: '',
      pullRequestUrl: pullRequestUrl,
    };
  };

  createPolicy = async (formData: {
    policyName: string;
    shortName: string;
    description: string;
    email: string;
    githubName: string;
    env: string;
  }) => {
    const currentDate = new Date();
    const timestamp = currentDate.toISOString().replace(/[-:.TZ]/g, '');
    const branchName = `${formData.shortName}-${timestamp}`;
    let pullRequestUrl = '';

    try {
      await this.unifiedGithubApi.createBranch({ owner, repo: repoName, branchName, fromRef: `heads/${formData.env}` });

      const codeownersData = await this.unifiedGithubApi.getFileInfo({
        owner,
        repo: repoName,
        filePath: '.github/CODEOWNERS',
        ref: branchName,
      });

      if (Array.isArray(codeownersData) || !('content' in codeownersData)) {
        throw new Error(`Failed to fetch data.json from data structure: ${JSON.stringify(codeownersData)}`);
      }

      const parsedTeam = this.parseTeam(formData.githubName);

      const updatedCodeowners = `${Buffer.from(codeownersData.content, 'base64').toString()}\n\n${formData.policyName}/ @${owner}/${parsedTeam}`;

      await this.unifiedGithubApi.updateFile({
        owner,
        repo: repoName,
        path: '.github/CODEOWNERS',
        message: `Update CODEOWNERS with ${formData.policyName}`,
        content: Buffer.from(updatedCodeowners).toString('base64'),
        sha: codeownersData.sha,
        branch: branchName,
      });

      const jsonContent = {
        [formData.policyName]: {
          clients: [],
          owner: `@${owner}/${parsedTeam}`,
          shortname: formData.shortName,
          email: formData.email,
          description: formData.description,
        },
      };

      await this.unifiedGithubApi.createFile({
        owner,
        repo: repoName,
        path: `${formData.policyName}/clients.json`,
        message: `Create clients.json for ${formData.policyName}`,
        content: Buffer.from(JSON.stringify(jsonContent, null, 2)).toString('base64'),
        branch: branchName,
      });

      const pr = await this.unifiedGithubApi.openPullRequest({
        owner,
        repo: repoName,
        title: `fix: update CODEOWNERS and create policy dir with ${formData.policyName}`,
        head: branchName,
        base: formData.env,
      });

      pullRequestUrl = pr.html_url;

      await this.unifiedGithubApi.requestReviewers({
        owner,
        repo: repoName,
        pullNumber: pr.number,
        reviewers: [],
        teamReviewers: ['data-movement'],
      });
    } catch (error) {
      const typedError = error as Error;

      return {
        success: false,
        error: typedError.message,
        pullRequestUrl: '',
      };
    }

    return {
      success: true,
      error: '',
      pullRequestUrl: pullRequestUrl,
    };
  };

  getConnectorEnvDataJsonFile = async (env: string) => {
    const data = await this.unifiedGithubApi.getFileInfo({
      owner,
      repo: repoConnectorName,
      filePath: `root/cnntr/data.json`,
      ref: env,
    });

    if (Array.isArray(data) || !('content' in data)) {
      throw new Error(`Failed to fetch data.json from branch ${env}: not a file`);
    }

    return JSON.parse(Buffer.from(data.content, 'base64').toString());
  };

  registerConnector = async (formData: {
    connectorName: string;
    shortName: string;
    description: string;
    email: string;
    githubName: string;
    vaultNamespace: string;
    vaultSecretPath: string;
    authProvider: string;
    authURL: string;
    env: string;
  }) => {
    const currentDate = new Date();
    const timestamp = currentDate.toISOString().replace(/[-:.TZ]/g, '');
    const branchName = `${formData.shortName}-${timestamp}`;
    let pullRequestUrl = '';

    try {
      await this.unifiedGithubApi.createBranch({
        owner,
        repo: repoConnectorName,
        branchName,
        fromRef: `heads/${formData.env}`,
      });

      const filePath = `${formData.connectorName}/config.json`;

      let existingSha: string | undefined;
      try {
        const existingData = await this.unifiedGithubApi.getFileInfo({
          owner,
          repo: repoConnectorName,
          filePath,
          ref: branchName,
        });
        if (Array.isArray(existingData) || !('content' in existingData)) {
          throw new Error(`Failed to fetch config.json from data structure: ${JSON.stringify(existingData)}`);
        }
        existingSha = existingData.sha;
      } catch (error: any) {
        if (error?.status === 404) {
          existingSha = undefined;
        } else {
          throw error;
        }
      }

      const parsedTeam = this.parseTeam(formData.githubName);

      const jsonContent = {
        [formData.connectorName]: {
          owner: `@${owner}/${parsedTeam}`,
          shortname: formData.shortName,
          email: formData.email,
          description: formData.description,
          vaultNamespace: `automation/${formData.vaultNamespace}`,
          vaultSecretPath: formData.vaultSecretPath,
          authProvider: formData.authProvider,
          authURL: formData.authURL,
        },
      };

      const fileContent = Buffer.from(JSON.stringify(jsonContent, null, 2)).toString('base64');
      const fileMessage = `Update config.json for ${formData.connectorName}`;

      if (existingSha !== undefined) {
        await this.unifiedGithubApi.updateFile({
          owner,
          repo: repoConnectorName,
          path: filePath,
          message: fileMessage,
          content: fileContent,
          sha: existingSha,
          branch: branchName,
        });
      } else {
        await this.unifiedGithubApi.createFile({
          owner,
          repo: repoConnectorName,
          path: filePath,
          message: fileMessage,
          content: fileContent,
          branch: branchName,
        });
      }

      const pr = await this.unifiedGithubApi.openPullRequest({
        owner,
        repo: repoConnectorName,
        title: `fix: register connector dir with ${formData.connectorName}`,
        head: branchName,
        base: formData.env,
      });

      pullRequestUrl = pr.html_url;

      await this.unifiedGithubApi.requestReviewers({
        owner,
        repo: repoConnectorName,
        pullNumber: pr.number,
        reviewers: [],
        teamReviewers: ['data-movement'],
      });
    } catch (error) {
      const typedError = error as Error;

      return {
        success: false,
        error: typedError.message,
        pullRequestUrl: '',
      };
    }

    return {
      success: true,
      error: '',
      pullRequestUrl: pullRequestUrl,
    };
  };
}
